import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { SectionTitle } from "@sprout/typography.tsx";
import JobRecommendationCard, {
  type JobRecommendation,
} from "@components/dashboard/job_recommendation_card.tsx";
import useTranslation from "@hooks/use_translation.ts";
import { useSafePostHog } from "@hooks/use_posthog.ts";
import useRecommendations, { recommendationsQueryKey } from "@/queries/use_recommendations.ts";
import useRecommendationFeedback from "@/queries/use_recommendation_feedback.ts";

interface Props {
  appliedJobPostId: string;
  onSelect?: (job: JobRecommendation) => void;
}

export const ConfirmationRecommendations = ({ appliedJobPostId, onSelect }: Props) => {
  const { t } = useTranslation("jobSearch");
  const posthog = useSafePostHog();
  const queryClient = useQueryClient();
  const submitFeedback = useRecommendationFeedback("application_confirmation");

  const [arrivedInTime] = useState(
    () => queryClient.getQueryData(recommendationsQueryKey(appliedJobPostId)) !== undefined,
  );

  const { data: recommendations = [] } = useRecommendations(appliedJobPostId, {
    enabled: arrivedInTime,
  });

  const shownRef = useRef(new Set<number>());

  useEffect(() => {
    if (!posthog || !arrivedInTime) return;
    recommendations.forEach((job, index) => {
      if (shownRef.current.has(job.id)) return;
      shownRef.current.add(job.id);
      posthog.capture("card_shown", {
        card_type: "recommendation",
        source: "application_confirmation",
        position: index + 1,
        job_post_id: job.id,
        job_title: job.title,
        company_name: job.companyName,
        previously_viewed: job.viewed,
      });
    });
  }, [posthog, arrivedInTime, recommendations]);

  if (!arrivedInTime || recommendations.length === 0) return null;

  const handleViewJob = (event: React.MouseEvent, job: JobRecommendation, position: number) => {
    if (onSelect) {
      event.preventDefault();
      onSelect(job);
    }
    posthog?.capture("card_clicked", {
      card_type: "recommendation",
      source: "application_confirmation",
      position,
      job_post_id: job.id,
      job_title: job.title,
      company_name: job.companyName,
      previously_viewed: job.viewed,
    });
  };

  return (
    <div className="flex flex-col items-start gap-3 w-full">
      <SectionTitle>
        {t("applicationSuccess.subHeaderForRecommendations", { count: recommendations.length })}
      </SectionTitle>
      <div className="flex flex-col gap-3 w-full">
        {recommendations.map((job, index) => (
          <JobRecommendationCard
            key={job.id}
            job={job}
            compact
            isConfirmation
            onViewJob={(event) => handleViewJob(event, job, index + 1)}
            feedback={job.feedback}
            onFeedback={(next) => submitFeedback(job, next)}
          />
        ))}
      </div>
    </div>
  );
};

export default ConfirmationRecommendations;
